import { RefObject, useCallback, cloneElement, ReactElement, MouseEvent, KeyboardEvent } from 'react';
import { RenderBtnProps } from '../popover.types';

export type UseTriggerButtonCloneParams = {
  renderTriggerButton?: (btnProps: RenderBtnProps) => ReactElement;
  triggerRef: RefObject<HTMLButtonElement | null>;
  open: boolean;
  closing: boolean;
  disabled?: boolean;
  controlledOpen?: boolean;
  keepOpenOnTriggerToggle?: boolean;
  ignoreClasses: string[];
  handleOpen: () => void;
  handleClose: () => void;
  handleOpenWithArrowKeys: (event: KeyboardEvent) => void;
  onTriggerButtonClick?: (e: MouseEvent) => void;
  stopPropagation?: boolean;
};

type ElementProps = {
  onClick?: (event: MouseEvent) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  [key: string]: unknown;
};

/**
 * Handle cloning trigger button with all event handlers and props.
 * Manages complex event handler merging and conditional behavior.
 */
export function useTriggerButtonClone({
  renderTriggerButton,
  triggerRef,
  open,
  closing,
  disabled,
  controlledOpen,
  keepOpenOnTriggerToggle,
  ignoreClasses,
  handleOpen,
  handleClose,
  handleOpenWithArrowKeys,
  onTriggerButtonClick,
  stopPropagation,
}: UseTriggerButtonCloneParams): () => ReactElement | null {
  const clonedTriggerButton = useCallback(() => {
    if (!renderTriggerButton) return null;

    const triggerButton = renderTriggerButton({ open, setOpen: handleOpen, setClose: handleClose, closing });
    const props = triggerButton.props as ElementProps;
    const originalOnClick = props.onClick;
    const originalOnKeyDown = props.onKeyDown;

    const clonedProps = {
      ref: triggerRef,
      disabled,
      onClick: (event: MouseEvent) => {
        if (stopPropagation) {
          event.stopPropagation();
        }
        originalOnClick?.(event);
        onTriggerButtonClick?.(event);

        // if disabled, or controlledOpen is defined, then do not call open/close based on click
        // when controlledOpen is defined, we use that prop in a useEffect to determine if we call handleOpen/handleClose
        if (disabled || controlledOpen !== undefined) return;
        if (open) {
          if (keepOpenOnTriggerToggle) return;
          if (
            ignoreClasses.length > 0 &&
            event.target instanceof Element &&
            event.target.closest(ignoreClasses.join(', '))
          ) {
            return;
          }
          handleClose();
        } else {
          handleOpen();
        }
      },
      onKeyDown: (event: KeyboardEvent) => {
        originalOnKeyDown?.(event);
        handleOpenWithArrowKeys(event);
      },
    };

    return cloneElement(triggerButton, clonedProps as Partial<typeof triggerButton.props>);
  }, [
    closing,
    controlledOpen,
    disabled,
    handleClose,
    handleOpen,
    handleOpenWithArrowKeys,
    keepOpenOnTriggerToggle,
    open,
    renderTriggerButton,
    ignoreClasses,
    onTriggerButtonClick,
    triggerRef,
    stopPropagation,
  ]);

  return clonedTriggerButton;
}
